/* ================================================================
   LFJ THUMBNAIL FALLBACK (Checkpoint 59)
   ----------------------------------------------------------------
   Fixes a real, widespread bug: cards for entries with no `thumbnail`
   value were rendering `<img src="">` — a guaranteed broken-image
   icon in every browser, not just a missing-picture placeholder.
   46 of 64 current videos.json entries have no thumbnail at all, so
   this wasn't an edge case.

   Per direct instruction: when there's no provided thumbnail, always
   use a random part of the video instead. Two real, different paths:

     NATIVE / UPLOADED VIDEO (a real Shopify CDN mp4 url) — an
       off-screen <video> loads the file, seeks to a genuinely random
       point (15%-75% of duration, avoiding the often-black first and
       last frames), draws that frame to a canvas, and the resulting
       data URL becomes the <img> src. Fully client-side, no server
       round-trip. If the browser can't read the frame back (a CORS-
       tainted canvas, a load error) this fails silently and the
       caller's placeholder stays — never throws, never leaves a
       broken empty src behind.

     YOUTUBE VIDEO — a real, honest platform limit: a YouTube iframe
       is cross-origin, so there is no way to grab an arbitrary frame
       from it client-side (the canvas would be permanently tainted).
       The closest real equivalent is YouTube's own default thumbnail
       image (a real frame YouTube itself extracted), served free with
       no API key at a predictable URL. Not literally "random", but
       the honest, working substitute for a source this code can't
       reach into — flagging that plainly rather than silently doing
       something else and calling it the same thing.

   USAGE:
     LFJ_THUMB_FALLBACK.apply(imgEl, entry)
   entry is a raw videos.json/gallery.json record. Does nothing if
   entry.thumbnail is already set — this only fills the gap.
================================================================ */
(function () {
  if (window.LFJ_THUMB_FALLBACK) return;

  var frameCache = {}; /* video url -> generated data URL, so the same
                           entry rendered twice doesn't re-seek/re-grab */

  function isYouTubeEntry(entry) {
    if (entry.id) return true; /* lfj-archive.js's own YouTube flag */
    var url = entry.videoUrl || entry.src || '';
    return /youtu\.?be/.test(url);
  }

  function youTubeId(entry) {
    if (entry.id) return entry.id;
    var url = entry.videoUrl || entry.src || '';
    var m = url.match(/(?:youtu\.be\/|v=)([\w-]+)/);
    return m ? m[1] : '';
  }

  function grabRandomFrame(url, onDone) {
    if (frameCache[url]) { onDone(frameCache[url]); return; }
    try {
      var video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';

      var settled = false;
      function fail() { if (!settled) { settled = true; onDone(null); } }
      /* Real files can stall; don't hang forever waiting on one. */
      var timeout = setTimeout(fail, 8000);

      video.addEventListener('loadedmetadata', function () {
        var dur = video.duration;
        if (!isFinite(dur) || dur <= 0) { fail(); return; }
        video.currentTime = dur * (0.15 + Math.random() * 0.6);
      });
      video.addEventListener('seeked', function () {
        if (settled) return;
        try {
          var canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 320;
          canvas.height = video.videoHeight || 180;
          canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
          var dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          settled = true;
          clearTimeout(timeout);
          frameCache[url] = dataUrl;
          onDone(dataUrl);
        } catch (e) {
          /* Canvas tainted by a cross-origin file with no CORS headers
             — a real limitation, not a bug in this code. */
          clearTimeout(timeout);
          fail();
        }
      });
      video.addEventListener('error', function () { clearTimeout(timeout); fail(); });
      video.src = url;
    } catch (e) { onDone(null); }
  }

  function apply(imgEl, entry) {
    if (!imgEl || !entry || entry.thumbnail) return;

    if (isYouTubeEntry(entry)) {
      var id = youTubeId(entry);
      if (id) imgEl.src = 'https://img.youtube.com/vi/' + id + '/hqdefault.jpg';
      return;
    }

    var url = entry.src || entry.videoUrl;
    if (!url) return; /* no video file to grab from — caller's placeholder stands */

    grabRandomFrame(url, function (dataUrl) {
      if (dataUrl) imgEl.src = dataUrl;
    });
  }

  /* Generic form for callers that don't have an <img> to hand off to —
     e.g. an element styled with a CSS background-image instead. */
  function resolve(entry, onUrl) {
    if (!entry || typeof onUrl !== 'function') return;
    if (entry.thumbnail) { onUrl(entry.thumbnail); return; }

    if (isYouTubeEntry(entry)) {
      var id = youTubeId(entry);
      if (id) onUrl('https://img.youtube.com/vi/' + id + '/hqdefault.jpg');
      return;
    }

    var url = entry.src || entry.videoUrl;
    if (!url) return;
    grabRandomFrame(url, function (dataUrl) { if (dataUrl) onUrl(dataUrl); });
  }

  window.LFJ_THUMB_FALLBACK = { apply: apply, resolve: resolve };
}());
